import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { Oval as Loader } from "react-loader-spinner";
import LazyImage from "../../../components/LazyImage/LazyImage";
import { useNavigation } from "../../../shared/utils/useNavigation";
import { DEFAULT_USER_AVATAR, ROLE } from "../../../shared/constants/common";
import { formatTimeAgo } from "../../../shared/utils/function";
import { useModalOpen } from '../../../shared/utils/modalUtils.js';
import { fetchFollowersList } from "../../../shared/actions/followAction";
import { useInfiniteScroll } from "../../../shared/hooks/useInfiniteScroll";
import {
  DEFAULT_LIST_PAGE_LIMIT,
  mergeUniqueById,
  resolveSkipLimitPagination,
} from "../../../shared/utils/listPagination";

const FALLBACK_ERROR = "Failed to fetch followers.";
const SEARCH_DEBOUNCE_MS = 250;

const getFollowersFromResponse = (data) => {
  if (!data) return [];

  if (Array.isArray(data?.followers)) return data.followers;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;

  return [];
};

const readKnownTotal = (pagination, data) => {
  const candidates = [
    pagination?.total,
    pagination?.recordsTotal,
    pagination?.totalCount,
    pagination?.totalFollowers,
    data?.totalFollowers,
    data?.total,
  ];
  const match = candidates.find((value) => Number.isFinite(Number(value)));
  return match == null ? null : Number(match);
};

const getFollowerIdentity = (follower) => {
  const user = follower?.user || {};
  return follower?.user_id || user?._id || user?.id || follower?._id || follower?.id || null;
};

const formatSubtitle = (creatorRole, subtitle, fallback) => {
  if (!subtitle) return fallback;

  const relativeTime = formatTimeAgo(subtitle);
  if (!relativeTime) return fallback;

  if (relativeTime === "now") {
    return `${creatorRole === ROLE.CREATOR_SUB ? "Subscribed just now" : "Following just now"}`;
  }

  return `${creatorRole === ROLE.CREATOR_SUB ? "Subscribed" : "Following"} for ${relativeTime}`;
};

const getFollowersLabel = (count, creatorRole) => {
  const isSubscriber = creatorRole === ROLE.CREATOR_SUB;
  const isPlural = count !== 1;

  if (isSubscriber) {
    return isPlural ? "Subscribers" : "Subscriber";
  }

  return isPlural ? "Followers" : "Follower";
};

const normalizeFollower = (follower) => {
  const user = follower?.user || follower || {};
  const username = user?.username || user?.userName || "unknown";
  const name = user?.name || user?.fullName || username;
  const user_id = follower?.user_id || null;
  const avatar =
    follower?.profile_picture ||
    user?.profilePicture ||
    user?.profile_picture ||
    DEFAULT_USER_AVATAR;

  return {
    id: user?._id || follower?.id || follower?._id || username,
    username,
    name,
    avatar,
    user_id,
    subtitle: follower?.followingSince || follower?.createdAt || follower?.since || ""
  };
};

function FollowersListModal({
  isOpen = false,
  onClose,
  creatorId = null,
  creatorRole = null,
  title = "Followers",
  totalCount = null,
}) {
  const navigate = useNavigation();
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const sessionRef = useRef(0);
  const pagingRef = useRef({
    nextSkip: 0,
    hasMore: false,
    loading: false,
  });
  const searchRef = useRef("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [followersList, setFollowersList] = useState([]);
  const [knownTotal, setKnownTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(isOpen);

  const stopPropagation = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleSearchChange = useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [query]);

  searchRef.current = debouncedQuery;

  const handleMessageClick = useCallback(
    (userId) => {
      navigate.toChat(userId);
    },
    [navigate]
  );

  const applyFollowersPage = useCallback((response, { append, requestedSkip, session }) => {
    if (session !== sessionRef.current) return false;

    const incoming = getFollowersFromResponse(response?.data);
    const page = resolveSkipLimitPagination({
      pagination: response?.pagination,
      itemCount: incoming.length,
      requestedSkip,
      requestedLimit: DEFAULT_LIST_PAGE_LIMIT,
    });

    let appendedNothingNew = false;
    setFollowersList((current) => {
      if (!append) return incoming;
      const next = mergeUniqueById(current, incoming, getFollowerIdentity);
      appendedNothingNew = next.length === current.length;
      return next;
    });

    const total = readKnownTotal(response?.pagination, response?.data);
    if (!append || total != null) {
      setKnownTotal(total);
    }

    const hasNextPage = page.hasMore
      && incoming.length > 0
      && page.nextSkip > requestedSkip
      && !appendedNothingNew;

    pagingRef.current.nextSkip = page.nextSkip;
    pagingRef.current.hasMore = hasNextPage;
    setHasMore(hasNextPage);
    return true;
  }, []);

  const resetList = useCallback(() => {
    pagingRef.current = { nextSkip: 0, hasMore: false, loading: false };
    searchRef.current = "";
    setQuery("");
    setDebouncedQuery("");
    setFollowersList([]);
    setKnownTotal(null);
    setHasMore(false);
    setIsLoading(false);
    setIsLoadingMore(false);
    setError("");
    setLoadMoreError("");
  }, []);

  useEffect(() => {
    const session = ++sessionRef.current;

    if (!isOpen) {
      resetList();
      return undefined;
    }

    if (!creatorId) {
      resetList();
      return undefined;
    }

    let cancelled = false;

    const loadFirstPage = async () => {
      pagingRef.current = { nextSkip: 0, hasMore: false, loading: true };
      if (listRef.current) listRef.current.scrollTop = 0;
      setIsLoading(true);
      setError("");
      setLoadMoreError("");
      setFollowersList([]);
      setKnownTotal(null);
      setHasMore(false);

      try {
        const response = await fetchFollowersList(creatorId, {
          skip: 0,
          limit: DEFAULT_LIST_PAGE_LIMIT,
          search: debouncedQuery,
        });

        if (cancelled || session !== sessionRef.current) return;

        if (!response?.success) {
          setFollowersList([]);
          setKnownTotal(null);
          setHasMore(false);
          setError(response?.error || response?.message || FALLBACK_ERROR);
          return;
        }

        applyFollowersPage(response, { append: false, requestedSkip: 0, session });
      } catch (err) {
        if (cancelled || session !== sessionRef.current) return;
        setFollowersList([]);
        setKnownTotal(null);
        setHasMore(false);
        setError(err?.message || FALLBACK_ERROR);
      } finally {
        if (!cancelled && session === sessionRef.current) {
          pagingRef.current.loading = false;
          setIsLoading(false);
        }
      }
    };

    loadFirstPage();

    return () => {
      cancelled = true;
    };
  }, [isOpen, creatorId, debouncedQuery, applyFollowersPage, resetList]);

  const loadMore = useCallback(async () => {
    const paging = pagingRef.current;
    if (!isOpen || !creatorId || paging.loading || !paging.hasMore) return;

    const session = sessionRef.current;
    const requestedSkip = paging.nextSkip;
    paging.loading = true;
    setIsLoadingMore(true);
    setLoadMoreError("");

    try {
      const response = await fetchFollowersList(creatorId, {
        skip: requestedSkip,
        limit: DEFAULT_LIST_PAGE_LIMIT,
        search: searchRef.current,
      });

      if (session !== sessionRef.current) return;

      if (!response?.success) {
        setLoadMoreError(response?.error || response?.message || FALLBACK_ERROR);
        return;
      }

      applyFollowersPage(response, { append: true, requestedSkip, session });
    } catch (err) {
      if (session !== sessionRef.current) return;
      setLoadMoreError(err?.message || FALLBACK_ERROR);
    } finally {
      if (session === sessionRef.current) {
        pagingRef.current.loading = false;
        setIsLoadingMore(false);
      }
    }
  }, [applyFollowersPage, creatorId, isOpen]);

  useInfiniteScroll({
    sentinelRef,
    rootRef: listRef,
    hasMore,
    isLoading: isLoading || isLoadingMore,
    onLoadMore: loadMore,
    enabled: isOpen && !error && !loadMoreError,
  });

  const normalizedFollowers = useMemo(
    () => followersList.map(normalizeFollower),
    [followersList]
  );

  const profileTotal = Number.isFinite(Number(totalCount)) ? Number(totalCount) : null;
  const displayedFollowersCount = debouncedQuery
    ? (knownTotal ?? normalizedFollowers.length)
    : (knownTotal ?? profileTotal ?? normalizedFollowers.length);
  const hasNoResults = !isLoading && !isLoadingMore && !error && normalizedFollowers.length === 0;
  const listLabel = title.toLowerCase();

  if (!isOpen) return null;

  return createPortal(
    <div className="followers-popup-overlay cm_bg_blur" >
      <div className="followers-popup-container" onClick={stopPropagation}>
        <div className="followers-popup-header">
          <h3 className="followers-popup-title">{title}</h3>
          <button type="button" className="followers-popup-close" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div className="followers-popup-search">
          <div className="followers-popup-search-container">
            <div className="followers-popup-search-icon">
              <i className="fa-solid fa-magnifying-glass"></i>
            </div>
            <input
              type="text"
              placeholder={`Search ${listLabel}...`}
              className="followers-popup-search-input"
              value={query}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="followers-popup-count">
          {isLoading
            ? `Loading ${listLabel}...`
            : `${displayedFollowersCount} ${getFollowersLabel(displayedFollowersCount, creatorRole)}`}
        </div>

        {error && <div className="followers-error">{error}</div>}

        <div className="followers-list" ref={listRef}>
          {isLoading ? (
            <div className="followers-loading">
              <Loader type="Oval" color="#F334BE" height={48} width={48} />
            </div>
          ) : (
            <>
              {hasNoResults && <div className="followers-empty">No {listLabel} found</div>}

              {normalizedFollowers.map((follower, index) => {
                const key = follower.id || `${follower.username}-${index}`;

                return (
                  <div className="followers-item" key={key}>
                    <div className="followers-user">
                      <LazyImage
                        src={follower.avatar}
                        alt={follower.username}
                        className="followers-avatar"
                      />
                      <div className="followers-meta">
                        <div
                          className="followers-username"
                        >
                          @{follower.username}
                        </div>
                        <div className="followers-subtext">
                          {formatSubtitle(creatorRole, follower.subtitle, follower.name)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="followers-message-btn"
                      title="Message"
                      onClick={() => {
                        handleMessageClick(follower.user_id);
                      }}
                    >
                      <i className="fa-solid fa-comment"></i>
                    </button>
                  </div>
                );
              })}

              {(hasMore || isLoadingMore || loadMoreError) && (
                <div className="followers-load-more">
                  {isLoadingMore && (
                    <Loader type="Oval" color="#F334BE" height={28} width={28} />
                  )}
                  {loadMoreError && (
                    <>
                      <div className="followers-error">{loadMoreError}</div>
                      <button
                        type="button"
                        className="followers-load-more-retry"
                        onClick={loadMore}
                      >
                        Try again
                      </button>
                    </>
                  )}
                  {hasMore && !loadMoreError && (
                    <div ref={sentinelRef} className="followers-load-more-sentinel" />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default FollowersListModal;


